<template>
  <div class="p-6 text-gray-200">
    <h3 class="text-lg font-medium leading-6">Delete List</h3>
    <div class="mt-2">
      <p>
        Are you sure you want to delete
        <span class="font-bold">{{ list?.title || 'this list' }}</span
        >?
      </p>
      <p class="mt-1 text-sm text-gray-400 italic">This can't be undone</p>
    </div>

    <!-- Error message -->
    <div
      v-if="deleteError"
      class="p-3 mt-4 text-sm rounded-lg cursor-pointer bg-red-500/20 border border-red-500/50 text-red-200"
      @click="deleteError = null">
      <div class="flex items-center gap-2">
        <fa-icon icon="exclamation-circle" />
        <span>{{ errorMessage }}</span>
      </div>
      <p class="text-xs text-red-300/70 mt-1">Tap to dismiss</p>
    </div>

    <div class="flex gap-x-3 mt-6">
      <button
        type="button"
        class="flex flex-1 justify-center items-center py-3 px-4 text-sm font-medium text-gray-400 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-700 hover:text-gray-300 transition ease-in-out"
        @click="closeModal">
        Cancel
      </button>
      <button
        type="button"
        :disabled="isDeleting"
        class="flex flex-[2] gap-x-2 justify-center items-center py-3 px-4 text-sm font-semibold text-white rounded-lg border border-transparent transition ease-in-out bg-linear-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 disabled:opacity-50 disabled:pointer-events-none"
        @click="removeList">
        <template v-if="isDeleting">
          Deleting...
          <fa-icon icon="spinner-third" class="fa-spin" />
        </template>
        <template v-else>
          Delete
          <fa-icon icon="trash" />
        </template>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStoreLists } from '@/stores/storeLists'
import { useRouter, useRoute } from 'vue-router'
import { useHapticFeedback } from '@/composables/useHapticFeedback'

const router = useRouter()
const route = useRoute()
const haptics = useHapticFeedback()
const isDeleting = ref(false)
const deleteError = ref(null)

const errorMessage = computed(() => {
  if (!deleteError.value) return ''
  if (typeof deleteError.value === 'string') return deleteError.value
  return (
    deleteError.value?.message || 'Failed to delete list. Please try again.'
  )
})

const props = defineProps({
  list: Object
})

const emit = defineEmits(['close'])

const storeLists = useStoreLists()

const closeModal = () => {
  haptics.light()
  emit('close')
}

// Function to remove the list
async function removeList() {
  if (isDeleting.value) return

  haptics.medium()
  isDeleting.value = true

  const deletedListId = props.list.id
  const deletedListSlug = props.list.slug

  try {
    await storeLists.deleteList(deletedListId)

    haptics.success()
    closeModal()

    // Check if we're on the list detail route for the deleted list
    const routeListId = route.params.listId
    const isOnDeletedListRoute =
      route.name === 'WebUserListSingle' &&
      (routeListId === deletedListId || routeListId === deletedListSlug)

    if (isOnDeletedListRoute) {
      // Redirect to user's lists page
      router.push({ name: 'WebLists' })
    }
  } catch (error) {
    haptics.error()
    isDeleting.value = false
    deleteError.value = error
    console.error('Error deleting list:', error)
  }
}
</script>
